import React, { useMemo, useState } from "react";

function getPasswordStrength(password) {
  let score = 0;
  if (password.length >= 10) score += 1;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score += 1;
  if (/\d/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;
  return score;
}

function invitedNameParts(displayName) {
  const parts = String(displayName || "").trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts.shift() || "",
    surname: parts.join(" "),
  };
}

export default function TenantUserRegistrationAccountForm({
  invitation,
  onSubmit,
  submitting,
}) {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const strength = useMemo(() => getPasswordStrength(password), [password]);
  const names = useMemo(
    () => invitedNameParts(invitation?.displayName),
    [invitation?.displayName],
  );
  const handleSubmit = (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    onSubmit({
      firstName: String(data.get("firstName") || "").trim(),
      surname: String(data.get("surname") || "").trim(),
      email: invitation.email,
      password,
    });
  };

  return (
    <form className="pa-form pa-registration-form" onSubmit={handleSubmit}>
      <div className="pa-field-grid">
        <label className="pa-field">
          <span>First name</span>
          <input
            name="firstName"
            autoComplete="given-name"
            defaultValue={names.firstName}
            required
          />
        </label>
        <label className="pa-field">
          <span>Surname</span>
          <input
            name="surname"
            autoComplete="family-name"
            defaultValue={names.surname}
            required
          />
        </label>
      </div>

      <label className="pa-field">
        <span>Work email</span>
        <input
          name="email"
          type="email"
          autoComplete="email"
          value={invitation.email || ""}
          readOnly
          aria-readonly="true"
          required
        />
        <small>
          This address is fixed by the secure invitation for {invitation.companyDisplayName || "this workspace"}.
        </small>
      </label>

      <div className="pa-invitation-workspace">
        <span>Invited workspace</span>
        <strong>{invitation.companyDisplayName || "AndUs tenant"}</strong>
        <small>
          {[invitation.productLabel, invitation.roleLabel]
            .filter(Boolean)
            .join(" · ")}
        </small>
      </div>

      <label className="pa-field">
        <span>Create password</span>
        <div className="pa-input-control is-plain">
          <input
            name="password"
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            minLength={10}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword((visible) => !visible)}
          >
            {showPassword ? "Hide" : "Show"}
          </button>
        </div>
        <div className="pa-password-strength" data-strength={strength}>
          {[1, 2, 3, 4].map((level) => (
            <span
              key={level}
              className={strength >= level ? "is-active" : ""}
            />
          ))}
          <small>
            {password ? ["", "Basic", "Good", "Strong", "Excellent"][strength] :
              "Use 10+ characters, uppercase, a number and a symbol"}
          </small>
        </div>
      </label>

      <label className="pa-check">
        <input name="terms" type="checkbox" required />
        <span aria-hidden="true" />
        <span>
          I agree to the AndUs Terms of Use and Privacy Notice supplied by my
          organisation.
        </span>
      </label>

      <button className="pa-primary-button" type="submit" disabled={submitting}>
        <span>{submitting ? "Creating account…" : "Continue to verification"}</span>
        <span aria-hidden="true">›</span>
      </button>
    </form>
  );
}

export { getPasswordStrength, invitedNameParts };
